import { MULTIPART_CHUNK_SIZE, MULTIPART_UPLOAD_THRESHOLD } from "@/config/fileUpload";
import type { DocumentCategory } from "@/types/document";
import { apiUrl } from "@/utils/api";

const CHUNK_SIZE = MULTIPART_CHUNK_SIZE;
const MULTIPART_THRESHOLD = MULTIPART_UPLOAD_THRESHOLD;
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

export interface MultipartUploadPart {
    partNumber: number;
    eTag: string;
}

export interface UploadProgress {
    loaded: number;
    total: number;
    percentage: number;
}

export interface MultipartUploadResult {
    key: string;
    filename: string;
}

/**
 * Determines if a file should use multipart upload based on size
 */
export function shouldUseMultipart(fileSize: number): boolean {
    return fileSize > MULTIPART_THRESHOLD;
}

/**
 * Delay utility for retry logic
 */
function delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Upload a single part with retry logic
 */
async function uploadPart(
    url: string,
    chunk: Blob,
    contentType: string,
    retries: number = MAX_RETRIES,
): Promise<string> {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= retries; attempt++) {
        try {
            const response = await fetch(url, {
                method: "PUT",
                body: chunk,
                headers: {
                    "Content-Type": contentType,
                },
            });

            if (!response.ok) {
                throw new Error(`Upload failed with status ${response.status}`);
            }

            const eTag = response.headers.get("ETag");
            if (!eTag) {
                throw new Error("No ETag in response");
            }

            return eTag.replace(/"/g, ""); // Remove quotes from ETag
        } catch (error) {
            lastError = error instanceof Error ? error : new Error(String(error));

            if (attempt < retries) {
                await delay(RETRY_DELAY * (attempt + 1)); // Exponential backoff
            }
        }
    }

    throw lastError || new Error("Upload failed after retries");
}

export interface MultipartUploadParams {
    idGlobalUUID: string;
    tag: string;
    category: DocumentCategory;
}

/**
 * Upload a file using multipart upload
 */
export async function uploadFileMultipart(
    file: File,
    params: MultipartUploadParams,
    onProgress?: (progress: UploadProgress) => void,
    authenticatedFetch?: typeof fetch,
): Promise<MultipartUploadResult> {
    const fetchFn = authenticatedFetch || fetch;
    try {
        // Step 1: Initiate multipart upload
        const initResponse = await fetchFn(apiUrl("/s3/initiate-multipart-upload"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                filename: file.name,
                contentType: file.type,
                idGlobalUUID: params.idGlobalUUID,
                tag: params.tag,
                category: params.category,
            }),
        });

        if (!initResponse.ok) {
            throw new Error("Failed to initiate multipart upload");
        }

        const { uploadId, key, bucket, finalFilename } = await initResponse.json();

        // Step 2: Split file into chunks and upload
        const parts: MultipartUploadPart[] = [];
        const numChunks = Math.ceil(file.size / CHUNK_SIZE);
        let uploadedBytes = 0;

        try {
            for (let partNumber = 1; partNumber <= numChunks; partNumber++) {
                const start = (partNumber - 1) * CHUNK_SIZE;
                const end = Math.min(start + CHUNK_SIZE, file.size);
                const chunk = file.slice(start, end);

                // Get presigned URL for this part
                const urlResponse = await fetchFn(apiUrl("/s3/generate-part-url"), {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        bucket,
                        key,
                        uploadId,
                        partNumber,
                    }),
                });

                if (!urlResponse.ok) {
                    throw new Error(`Failed to get URL for part ${partNumber}`);
                }

                const { url } = await urlResponse.json();

                // Upload the part with retry logic
                const eTag = await uploadPart(url, chunk, file.type);

                parts.push({ partNumber, eTag });
                uploadedBytes += chunk.size;

                // Report progress
                if (onProgress) {
                    onProgress({
                        loaded: uploadedBytes,
                        total: file.size,
                        percentage: Math.round((uploadedBytes / file.size) * 100),
                    });
                }
            }

            // Step 3: Complete multipart upload
            const completeResponse = await fetchFn(apiUrl("/s3/complete-multipart-upload"), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    bucket,
                    key,
                    uploadId,
                    parts,
                }),
            });

            if (!completeResponse.ok) {
                throw new Error("Failed to complete multipart upload");
            }

            const result = await completeResponse.json();

            return {
                key: result.key || key,
                filename: finalFilename,
            };
        } catch (error) {
            // Abort the multipart upload on error
            try {
                await fetchFn(apiUrl("/s3/abort-multipart-upload"), {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        bucket,
                        key,
                        uploadId,
                    }),
                });
            } catch (abortError) {
                console.error("Failed to abort multipart upload:", abortError);
            }

            throw error;
        }
    } catch (error) {
        throw new Error(
            `Multipart upload failed: ${error instanceof Error ? error.message : String(error)}`,
        );
    }
}
